import { Dispatch, FC } from 'react';
import { useTranslation } from 'react-i18next';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import ProviderKey from '@/shared/types/fulfilment/providerKey.enum';
import SupportedProvider from '@/shared/types/fulfilment/supportedProvider.type';

import { FormAction, FormState } from '../reducers/formReducer';
import FulfilmentProviderDetails from '../types/fulfilmentProviderDetails.type';
import FormFieldWithTooltip from './FormFieldWithTooltip';

type FulfilmentProviderDetailsFormProps = {
  supportedProviders: SupportedProvider[];
  formState: FormState;
  dispatchForm: Dispatch<FormAction>;
};

const FulfilmentProviderDetailsForm: FC<FulfilmentProviderDetailsFormProps> = ({
  supportedProviders,
  formState,
  dispatchForm,
}) => {
  const { t } = useTranslation(['fulfilmentProviders', 'common']);

  const {
    fulfilmentProviderDetails: {
      fulfilment_provider_key,
      provider_name,
      provider_description,
      is_auto_fulfil,
      is_force_inventory_sync,
      fulfilment_update_interval,
      fulfilment_delay_time,
    },
    fulfilmentProviderDetailsErrors,
  } = formState;

  const onValidate = (fieldName: string) => {
    const errors: Partial<FulfilmentProviderDetails> = {};

    if (fieldName === 'fulfilment_provider_key') {
      !fulfilment_provider_key
        ? (errors.fulfilment_provider_key = t(
            'fulfilmentProviders:fulfilmentProviderKeyRequired',
          ))
        : (errors.fulfilment_provider_key = '');
    }

    if (fieldName === 'provider_name') {
      !provider_name
        ? (errors.provider_name = t('fulfilmentProviders:providerNameRequired'))
        : (errors.provider_name = '');
    }

    if (fieldName === 'provider_description') {
      !provider_description
        ? (errors.provider_description = t(
            'fulfilmentProviders:providerDescriptionRequired',
          ))
        : (errors.provider_description = '');
    }

    dispatchForm({
      type: 'SET_FULFILMENT_PROVIDER_DETAILS_ERRORS',
      payload: { ...fulfilmentProviderDetailsErrors, ...errors },
    });
  };

  return (
    <WaveUIForm_V2
      fields={[
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          field_type: FieldType.Select,
          field_props: {
            label: t('fulfilmentProviders:fulfilment_provider_key'),
            value: fulfilment_provider_key,
            name: 'fulfilment_provider_key',
            options: supportedProviders.map((provider) => {
              const option = {
                label: provider.fulfilment_provider_name,
                value: provider.fulfilment_provider_key,
              };

              if (provider.fulfilment_provider_key === ProviderKey.SHIP) {
                return { ...option, disabled: true };
              }

              return option;
            }),
            required: true,
            placeholder: `${t('common:select')} ${t('fulfilmentProviders:fulfilment_provider_key')}`,
            error: !!fulfilmentProviderDetailsErrors.fulfilment_provider_key,
            helperText: fulfilmentProviderDetailsErrors.fulfilment_provider_key,
            onBlur: () => onValidate('fulfilment_provider_key'),
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          field_type: FieldType.Text,
          field_props: {
            label: t('fulfilmentProviders:provider_name'),
            value: provider_name,
            name: 'provider_name',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:provider_name')}`,
            required: true,
            error: !!fulfilmentProviderDetailsErrors.provider_name,
            helperText: fulfilmentProviderDetailsErrors.provider_name,
            onBlur: () => onValidate('provider_name'),
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          field_type: FieldType.Text,
          field_props: {
            label: t('fulfilmentProviders:provider_description'),
            value: provider_description,
            name: 'provider_description',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:provider_description')}`,
            required: true,
            error: !!fulfilmentProviderDetailsErrors.provider_description,
            helperText: fulfilmentProviderDetailsErrors.provider_description,
            onBlur: () => onValidate('provider_description'),
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          columns: 'auto',
          field_type: FieldType.Switch,
          field_props: {
            label: t('fulfilmentProviders:is_auto_fulfil'),
            checked: is_auto_fulfil,
            name: 'is_auto_fulfil',
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          columns: 'auto',
          field_type: FieldType.Switch,
          field_props: {
            label: t('fulfilmentProviders:is_force_inventory_sync'),
            checked: is_force_inventory_sync,
            name: 'is_force_inventory_sync',
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          isVisible: is_force_inventory_sync,
          field_type: FieldType.Number,
          field_props: {
            label: t('fulfilmentProviders:fulfilment_update_interval'),
            value: is_force_inventory_sync
              ? fulfilment_update_interval
              : '2400',
            name: 'fulfilment_update_interval',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:fulfilment_update_interval')}`,
          },
        },
        {
          columnRenderer: (field) => <FormFieldWithTooltip field={field} />,
          isVisible: is_auto_fulfil,
          field_type: FieldType.Number,
          field_props: {
            label: t('fulfilmentProviders:fulfilment_delay_time'),
            value: is_auto_fulfil ? fulfilment_delay_time : '7200',
            name: 'fulfilment_delay_time',
            placeholder: `${t('common:enter')} ${t('fulfilmentProviders:fulfilment_delay_time')}`,
          },
        },
      ]}
      getFieldsValues={(value) => {
        dispatchForm({
          type: 'SET_FULFILMENT_PROVIDER_DETAILS',
          payload: { ...formState.fulfilmentProviderDetails, ...value },
        });
      }}
    />
  );
};

export default FulfilmentProviderDetailsForm;
